import { createContext, useContext, useEffect, useState } from "react";
import orderService from "../service/orderService";
import { useAuth } from "./AuthContext";

const OrderContext = createContext();

export const OrderProvider = ({ children }) => {
  const { token, authStatus } = useAuth();

  const [orders, setOrders] = useState(() => {
    const saved = localStorage.getItem("orders");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("orders", JSON.stringify(orders));
  }, [orders]);

  useEffect(() => {
    let cancelled = false;
    const loadOrders = async () => {
      try {
        if (!token) {
          setOrders([]);
          return;
        }
        if (authStatus !== "ready") return;
        const data = await orderService.getUserOrders(token);
        if (!cancelled) setOrders(data);
      } catch (err) {
        console.error("❌ Error fetching orders:", err);
      }
    };
    loadOrders();
    return () => { cancelled = true; };
  }, [token, authStatus]);

  const createOrder = async (cartItems, total) => {
    try {
      const newOrder = await orderService.createOrder(cartItems, total, token);
      setOrders((prev) => [...prev, newOrder]);
      return newOrder;
    } catch (err) {
      console.error("❌ Error creating order:", err);
      throw err;
    }
  };

  const updateOrderDate = async (orderId, date) => {
    try {
      const updated = await orderService.updateOrderDate(orderId, date, token);
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      return updated;
    } catch (err) {
      console.error("❌ Error updating order:", err);
      throw err;
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      const canceled = await orderService.cancelOrder(orderId, token);
      setOrders((prev) =>
        prev.map((o) => (o.id === canceled.id ? canceled : o))
      );
      return canceled;
    } catch (err) {
      console.error("❌ Error canceling order:", err);
      throw err;
    }
  };

  return (
    <OrderContext.Provider
      value={{
        orders,
        setOrders,
        createOrder,
        updateOrderDate,
        cancelOrder,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrders = () => useContext(OrderContext);
